import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';

// Menu a tendina a opzioni fisse (filtri, ordinamenti) con lo stesso aspetto
// del Combobox di Gruppo/Etichetta: stesso controllo chiuso e, aperto, lo
// stesso popup scuro con segno di spunta sull'opzione selezionata — invece
// del menu nativo del sistema operativo di un <select> normale.
function Select({ id, value, onChange, options, style, className = '', ariaLabel, placeholder }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const listboxId = `${id}-listbox`;

  useLayoutEffect(() => {
    if (!open) return;
    const updateRect = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  const commit = (v) => {
    onChange({ target: { value: v } });
    setOpen(false);
    btnRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % options.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i - 1 + options.length) % options.length); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (options[activeIndex]) commit(options[activeIndex].value); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Tab') { setOpen(false); }
  };

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`select-wrap ${className}`.trim()} style={style}>
      <button
        type="button"
        id={id}
        ref={btnRef}
        className="select select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
      >
        <span>{selected ? selected.label : placeholder}</span>
      </button>
      <Icon name="chevronRight" size={15} className="select-caret" />
      {open && rect && createPortal(
        <ul className="combobox-list" role="listbox" id={listboxId} style={{ top: rect.top, left: rect.left, minWidth: rect.width }}>
          {options.map((o, i) => (
            <li key={o.value} role="option" aria-selected={o.value === value}>
              <button
                type="button"
                className={`combobox-option${i === activeIndex ? ' is-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(o.value)}
              >
                <Icon name="check" size={14} className={`combobox-check${o.value === value ? '' : ' is-hidden'}`} />
                <span>{o.label}</span>
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default Select;
